import {array} from 'json-bouncer'
import * as R from 'ramda'
import {FeedbackLoop} from '../../Data/FeedbackLoop'
import {decodeHttpResponse, HttpError} from '../../Data/Http'
import {update} from '../../Data/ObjectHelper'
import {Result} from '../../Data/Result'
import * as WebData from '../../Data/WebData'
import {bpsUserDecoder} from '../../Entities/BpsUser'
import {companyDecoder} from '../../Entities/Company'
import {customerDecoder} from '../../Entities/Customer'
import * as BpsUserResource from '../../Request/BpsUserResource'
import * as CompanyResource from '../../Request/CompanyResource'
import * as CustomerResource from '../../Request/CustomerResource'
import * as OrderFilter from './Model/OrderFilter'
import * as OrderFilteringState from './Model/OrderFilteringState'
import * as OrderFilterInput from './Model/OrderFilterInput'
import {OrderFilteringAction} from './OrderFilteringActions'
import {
    OrderFilteringFeedback,
    OrderFilterRequestIdentifier
} from './OrderFilteringFeedback'

type OrderFilteringLoop =
    FeedbackLoop<OrderFilteringState.OrderFilteringState, OrderFilteringFeedback>

export function orderFilteringReducer(
    state: OrderFilteringState.OrderFilteringState,
    action: OrderFilteringAction,
): OrderFilteringLoop {
    switch (action.type) {
        case 'FilterSelectChanged':
            if (action.value !== state.selectedFilter) {
                const feedback: OrderFilteringFeedback[] =
                    action.value === 'Customer' ?
                        [{
                            type: 'SendRequest',
                            identifier: {type: 'CustomersList'},
                            httpRequest: CustomerResource.list(),
                        }] :
                        []
                return {
                    state: update(
                        {
                            selectedFilter: action.value,
                            addFilterInput:
                                OrderFilterInput.fromFilterType(action.value),
                        },
                        state,
                    ),
                    feedback,
                }
            } else {
                return {state, feedback: []}
            }
        case 'CompanySearchChanged':
            if (state.addFilterInput !== null &&
                state.addFilterInput.type === 'CompanyFilterInput'
            ) {
                return {
                    state: update(
                        {
                            addFilterInput: update(
                                {companySearch: action.value},
                                state.addFilterInput,
                            ),
                        },
                        state,
                    ),
                    feedback: [],
                }
            }
            return {state, feedback: []}
        case 'FetchCompanySuggestions': {
            const customerId: number | null =
                R.reduce<OrderFilter.OrderFilter, number | null>(
                    (previous, orderFilter) =>
                        orderFilter.type === 'CustomerFilter'
                            ? orderFilter.customer.id
                            : previous,
                    null,
                    state.activeFilters)

            if (state.addFilterInput !== null &&
                state.addFilterInput.type === 'CompanyFilterInput'
            ) {
                const
                    search = state.addFilterInput.companySearch,
                    httpRequest =
                        customerId !== null ?
                            CompanyResource.listSuggestionsForCustomer(
                                customerId,
                                search,
                            ) :
                            CompanyResource.listSuggestions(search)
                return {
                    state,
                    feedback: [{
                        type: 'SendRequest',
                        identifier: {type: 'CompanySuggestionList'},
                        httpRequest,
                    }]
                }
            }
            return {state, feedback: []}
        }
        case 'CompanySuggestionListResponse': {
            if (state.addFilterInput !== null &&
                state.addFilterInput.type === 'CompanyFilterInput'
            ) {
                const companies =
                    decodeHttpResponse(
                        array(companyDecoder()),
                        action.result,
                    )
                return {
                    state: update(
                        {
                            addFilterInput: update(
                                {
                                    companySuggestions:
                                        companies.type === 'Ok'
                                            ? companies.value
                                            : []
                                },
                                state.addFilterInput,
                            ),
                        },
                        state,
                    ),
                    feedback: [],
                }
            }
            return {state, feedback: []}
        }
        case 'ClearCompanySuggestions':
            if (state.addFilterInput !== null &&
                state.addFilterInput.type === 'CompanyFilterInput'
            ) {
                return {
                    state: update(
                        {
                            addFilterInput: update(
                                {companySuggestions: []},
                                state.addFilterInput,
                            ),
                        },
                        state,
                    ),
                    feedback: [],
                }
            }
            return {state, feedback: []}
        case 'BpsUserSearchChanged':
            if (state.addFilterInput !== null &&
                state.addFilterInput.type === 'BpsUserFilterInput'
            ) {
                return {
                    state: update(
                        {
                            addFilterInput: update(
                                {bpsUserSearch: action.value},
                                state.addFilterInput,
                            ),
                        },
                        state,
                    ),
                    feedback: [],
                }
            }
            return {state, feedback: []}
        case 'FetchBpsUserSuggestions': {
            const
                companyId: number | null =
                    R.reduce<OrderFilter.OrderFilter, number | null>(
                        (previous, orderFilter) =>
                            orderFilter.type === 'CompanyFilter'
                                ? orderFilter.company.id
                                : previous,
                        null,
                        state.activeFilters,
                    ),
                customerId: number | null =
                    R.reduce<OrderFilter.OrderFilter, number | null>(
                        (previous, orderFilter) =>
                            orderFilter.type === 'CustomerFilter'
                                ? orderFilter.customer.id
                                : previous,
                        null,
                        state.activeFilters,
                    )

            if (state.addFilterInput !== null &&
                state.addFilterInput.type === 'BpsUserFilterInput'
            ) {
                const
                    search = state.addFilterInput.bpsUserSearch,
                    httpRequest =
                        companyId !== null ?
                            BpsUserResource.listSuggestionsForCompany(
                                companyId,
                                search,
                            ) :
                            customerId !== null ?
                                BpsUserResource.listSuggestionsForCustomer(
                                    customerId,
                                    search,
                                ) :
                                BpsUserResource.listSuggestions(search)

                return {
                    state,
                    feedback: [{
                        type: 'SendRequest',
                        identifier: {
                            type: 'BpsUserSuggestionList'
                        },
                        httpRequest,
                    }]
                }
            }
            return {state, feedback: []}
        }
        case 'BpsUserSuggestionListResponse': {
            if (state.addFilterInput !== null &&
                state.addFilterInput.type === 'BpsUserFilterInput'
            ) {
                const bpsUsers =
                    decodeHttpResponse(
                        array(bpsUserDecoder()),
                        action.result,
                    )
                return {
                    state: update(
                        {
                            addFilterInput: update(
                                {
                                    type: 'BpsUserFilterInput',
                                    bpsUserSuggestions:
                                        bpsUsers.type === 'Ok'
                                            ? bpsUsers.value
                                            : [],
                                },
                                state.addFilterInput,
                            ),
                        },
                        state,
                    ),
                    feedback: [],
                }
            }
            return {state, feedback: []}
        }
        case 'ClearBpsUserSuggestions':
            if (state.addFilterInput !== null &&
                state.addFilterInput.type === 'BpsUserFilterInput'
            ) {
                return {
                    state: update(
                        {
                            addFilterInput: update(
                                {bpsUserSuggestions: []},
                                state.addFilterInput,
                            ),
                        },
                        state,
                    ),
                    feedback: [],
                }
            }
            return {state, feedback: []}
        case 'AddFilter': {
            const newState = OrderFilteringState.submit(state)
            return {
                state: newState,
                feedback: [{
                    type: 'FiltersChanged',
                    filters: newState.activeFilters,
                }],
            }
        }
        case 'ResetFilters': {
            const newState = OrderFilteringState.clearFilters(state)
            return {
                state: newState,
                feedback: [{
                    type: 'FiltersChanged',
                    filters: newState.activeFilters,
                }],
            }
        }
        case 'RemoveFilter': {
            const newState =
                OrderFilteringState.removeFilter(action.filterType, state)
            return {
                state: newState,
                feedback: [{
                    type: 'FiltersChanged',
                    filters: newState.activeFilters,
                }],
            }
        }
        case 'ListCustomersResponse':
            const customers =
                WebData.fromResult(
                    decodeHttpResponse(
                        array(customerDecoder()),
                        action.result,
                    ),
                )

            if (state.addFilterInput !== null &&
                state.addFilterInput.type === 'CustomerFilterInput'
            ) {
                return {
                    state: update(
                        {
                            addFilterInput: update(
                                {customers},
                                state.addFilterInput,
                            ),
                        },
                        state,
                    ),
                    feedback: [],
                }
            } else {
                return {state, feedback: []}
            }
        case 'InputChanged':
            return {
                state: OrderFilteringState.reduceFilterInputChanged(
                    action.subAction,
                    state,
                ),
                feedback: [],
            }
        case 'PopoverToggle':
            return {
                state: update({popoverOpen: action.open}, state),
                feedback: [],
            }
    }
}

export function orderFilterHttpResponseAction(
    identifier: OrderFilterRequestIdentifier,
    result: Result<HttpError, string>,
): OrderFilteringAction {
    switch (identifier.type) {
        case 'CustomersList':
            return {
                type: 'ListCustomersResponse',
                result,
            }
        case 'CompanySuggestionList':
            return {
                type: 'CompanySuggestionListResponse',
                result
            }
        case 'BpsUserSuggestionList':
            return {
                type: 'BpsUserSuggestionListResponse',
                result
            }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Components/OrderFiltering/OrderFilteringReducer.ts